import { useEffect, useCallback, useMemo, useRef } from 'react';
import { EditorView, ViewUpdate } from '@codemirror/view';
import { EditorManager, ICodeEditorProps } from './types';
import { useCodeMirror } from './useCodeMirror';

type OnChange = (value: string, viewUpdate: ViewUpdate) => void;

const onUpdate = (onChange: React.MutableRefObject<OnChange>) =>
	EditorView.updateListener.of((viewUpdate: ViewUpdate) => {
		if (viewUpdate.docChanged) {
			const { doc } = viewUpdate.state;
			const value = doc.toString();
			onChange.current(value, viewUpdate);
		}
	});

export const useCodeEditor = ({
	value,
	editable,
	extensions,
	theme,
	onChange,
	lang
}: ICodeEditorProps) => {
	const callbackRef = useRef(onChange);
	useEffect(() => {
		callbackRef.current = onChange;
	}, [onChange]);

	const { ref, view } = useCodeMirror(
		[onUpdate(callbackRef), ...extensions],
		editable,
		theme,
		lang
	);

	const insertInCursor = useCallback(
		(v: string) => {
			if (view) {
				const cursorFrom = view.state.selection.main.from;
				const cursorTo = view.state.selection.main.to;
				const transaction = view.state.update({
					changes: {
						from: cursorFrom,
						to: cursorTo > 0 ? cursorTo : undefined,
						insert: v
					},
					selection: { anchor: cursorFrom + v.length },
					scrollIntoView: true
				});

				if (transaction) {
					view.dispatch(transaction);
				}
			}
		},
		[view]
	);

	useEffect(() => {
		if (view) {
			const editorValue = view.state.doc.toString();

			if (value !== editorValue) {
				view.dispatch({
					changes: {
						from: 0,
						to: editorValue.length,
						insert: value || ''
					}
				});
			}
		}
	}, [value, view, theme, lang]);

	const realView = useMemo(
		() => ({ ...view, insertInCursor } as EditorManager),
		[view, insertInCursor]
	);

	return { ref, view: realView };
};

export default useCodeEditor;
